import React, { useEffect, useState } from "react";
import {
  TextField,
  MenuItem,
  CircularProgress,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Box
} from "@mui/material";
import logo from "../../styles/logo.png";

import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";

const BasicDetailsForm = ({ userId, onNext, onSkip }) => {
  const [formData, setFormData] = useState({});
  const [eyeColors, setEyeColors] = useState([]);
  const [hairColors, setHairColors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [ethnicities, setEthnicities] = useState([]);
  const [unions, setUnions] = useState([]);
  const [vocalRanges, setVocalRanges] = useState([]);
  const [vocalTypes, setVocalTypes] = useState([]);
  const [selectedVocalTypes, setSelectedVocalTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  console.log("BasicDetailsForm - Received userId:", userId);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (!userId) {
          console.error("No userId provided to BasicDetailsForm");
          setLoading(false);
          return;
        }

        console.log("Fetching profile for userId:", userId);
        
        // load profile
        const profileRes = await api.get(ENDPOINTS.getTalentProfile({ userId }));
        const profileData = profileRes.data?.data;
        console.log("Profile data received:", profileData);
        setProfile(profileData);

        // load lookups in parallel
        const [
          eyeRes,
          hairRes,
          locRes,
          ethRes,
          unionRes,
          vocalRes,
          vocalTypesRes,
          userVocalTypesRes
        ] = await Promise.all([
          api.get(ENDPOINTS.getEyeColors()),
          api.get(ENDPOINTS.getHairColors()),
          api.get(ENDPOINTS.getLocations()),
          api.get('/api/User/get-all-ethnicities'), // Updated endpoint
          api.get(ENDPOINTS.getUnions()),
          api.get('/api/User/get-vocal-ranges'), // New vocal range endpoint
          api.get('/api/User/GetVocalTypes'), // All available vocal types
          api.get(`/api/TalentProfile/GetUserVocalTypes/${userId}`) // User's selected vocal types
        ]);

        setEyeColors(eyeRes.data?.data || []);
        setHairColors(hairRes.data?.data || []);
        setLocations(locRes.data?.data || []);
        setEthnicities(ethRes.data?.data || []);
        setUnions(unionRes.data || []);
        
        console.log("Vocal Ranges API Response:", vocalRes.data);
        setVocalRanges(vocalRes.data?.data || []);
        console.log("Vocal Ranges set to:", vocalRes.data?.data || []);

        console.log("Vocal Types API Response:", vocalTypesRes.data);
        setVocalTypes(vocalTypesRes.data?.data || []);

        console.log("User Vocal Types API Response:", userVocalTypesRes.data);
        const userVocalTypes = userVocalTypesRes.data?.data || [];
        setSelectedVocalTypes(userVocalTypes.map(vt => vt.vocalTypeId || vt.id));

        // initialize formData from profile
        if (profileData) {
          const cleaned = Object.fromEntries(
            Object.entries(profileData).map(
              ([k, v]) => [k, v === "" ? null : v]
            )
          );
          setFormData(cleaned);
        }
      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, [userId]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (value === "" ? null : value)
    }));
  };

  const handleVocalTypeChange = (event) => {
    const value = event.target.value;
    setSelectedVocalTypes(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSubmit = async () => {
    console.log("Submitting form data:", formData);
    console.log("API Endpoint:", ENDPOINTS.updateTalentProfile());
    
    // Find the selected option objects to get their IDs
    const selectedEyeColor = eyeColors.find(e => e.eyeColorName === formData.eyeColor);
    const selectedHairColor = hairColors.find(h => h.hairColorName === formData.hairColor);
    const selectedEthnicity = ethnicities.find(e => e.ethnicityName === formData.ethnicity);

    const payload = {
      userId: parseInt(userId),
      firstName: formData.firstName || "",
      lastName: formData.lastName || "",
      middleName: formData.middleName || "", // Add this field to form
      sex: formData.sex || "",
      primaryContactNumber: profile?.primaryContactNumber || "",
      stateCode: formData.stateCode || "",
      email: profile?.email || "",
      ageRangeFrom: parseInt(formData.ageRangeFrom) || 0,
      ageRangeTo: parseInt(formData.ageRangeTo) || 0,
      eyeColorCode: selectedEyeColor?.eyeColorId || 0,
      hairColorCode: selectedHairColor?.hairColorId || 0,
      weight: parseFloat(formData.weight) || 0,
      heightStart: parseInt(formData.heightStart) || 0,
      heightEnd: parseInt(formData.heightEnd) || 0,
      vocalRangeID: parseInt(formData.vocalRangeID) || 0,
      dress: formData.sex === "Female" ? (formData.dress || "") : "",
      bust: formData.sex === "Female" ? (formData.bust || "") : "",
      waist: formData.waist || "",
      hips: formData.sex === "Female" ? (formData.hips || "") : "",
      shoe: formData.shoe || "",
      maleSuit: formData.sex === "Male" ? (formData.maleSuit || "") : "",
      maleShirt: formData.sex === "Male" ? (formData.maleShirt || "") : "",
      maleWaist: formData.sex === "Male" ? (formData.maleWaist || "") : "",
      maleInseam: formData.sex === "Male" ? (formData.maleInseam || "") : "",
      maleShoe: formData.sex === "Male" ? (formData.maleShoe || "") : "",
      ethnicityCode: selectedEthnicity?.ethnicityCode || 0,
      hasDriversLicense: formData.hasDriversLicense || false
    };

    console.log("Payload to be sent:", payload);

    try {
      const response = await api.post(ENDPOINTS.updateTalentProfile(), payload);
      console.log("API Response:", response);
      
      // Also save vocal types separately
      if (selectedVocalTypes.length > 0) {
        const vocalTypesPayload = {
          userId: parseInt(userId),
          userBy: parseInt(userId),
          vocalTypeIds: selectedVocalTypes.map(id => parseInt(id))
        };
        console.log("Vocal Types Payload:", vocalTypesPayload);
        
        const vocalTypesResponse = await api.post('/api/TalentProfile/AddorUpdateUserVocalTypes', vocalTypesPayload);
        console.log("Vocal Types API Response:", vocalTypesResponse);
      }
      
      if (typeof onNext === "function") {
        onNext();
      }
    } catch (err) {
      console.error("Submit failed:", err);
      console.error("Error response:", err.response?.data);
    }
  };

  if (loading) return <CircularProgress />;

  if (!userId) {
    return <div>Error: No user ID provided</div>;
  }

  return (
    <div className="shadow margin0" style={{ maxWidth: 1200, margin: "auto", padding: 20, backgroundColor: "#fff" }}>
      
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TextField
            label="First Name"
            name="firstName"
            fullWidth
            value={formData.firstName || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Middle Name"
            name="middleName"
            fullWidth
            value={formData.middleName || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Last Name"
            name="lastName"
            fullWidth
            value={formData.lastName || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Sex"
            name="sex"
            select
            fullWidth
            value={formData.sex || ""}
            onChange={handleChange}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Location"
            name="stateCode"
            select
            fullWidth
            value={formData.stateCode || ""}
            onChange={handleChange}
          >
            {locations.map(loc => (
              <MenuItem key={loc.locationCode} value={loc.locationCode}>
                {loc.locationCity}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={3}>
          <TextField
            label="Union Status"
            name="unionStatus"
            select
            fullWidth
            value={formData.unionStatus || ""}
            onChange={handleChange}
          >
            {unions.map((u, idx) => (
              <MenuItem key={idx} value={u.affiliationName}>
                {u.affiliationName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Height (ft)"
            name="heightStart"
            type="number"
            fullWidth
            value={formData.heightStart || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Height (in)"
            name="heightEnd"
            type="number"
            fullWidth
            value={formData.heightEnd || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Weight (lbs)"
            name="weight"
            type="number"
            fullWidth
            value={formData.weight || ""}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            label="Shoe Size"
            name="shoe"
            fullWidth
            value={formData.shoe || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Eye Color"
            name="eyeColor"
            select
            fullWidth
            value={formData.eyeColor || ""}
            onChange={handleChange}
          >
            {eyeColors.map(c => (
              <MenuItem key={c.eyeColorId} value={c.eyeColorName}>
                {c.eyeColorName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Hair Color"
            name="hairColor"
            select
            fullWidth
            value={formData.hairColor || ""}
            onChange={handleChange}
          >
            {hairColors.map(c => (
              <MenuItem key={c.hairColorId} value={c.hairColorName}>
                {c.hairColorName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Ethnicity"
            name="ethnicity"
            select
            fullWidth
            value={formData.ethnicity || ""}
            onChange={handleChange}
          >
            {ethnicities.map(e => (
              <MenuItem key={e.ethnicityCode} value={e.ethnicityName}>
                {e.ethnicityName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Age Range From"
            name="ageRangeFrom"
            type="number"
            fullWidth
            value={formData.ageRangeFrom || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Age Range To"
            name="ageRangeTo"
            type="number"
            fullWidth
            value={formData.ageRangeTo || ""}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            label="Vocal Range"
            name="vocalRangeName"
            select
            fullWidth
            value={formData.vocalRangeName || ""}
            onChange={handleChange}
          >
            {vocalRanges.map(v => (
              <MenuItem key={v.vocalRangeId} value={v.vocalRangeName}>
                {v.vocalRangeName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel>Vocal Types</InputLabel>
            <Select
              multiple
              value={selectedVocalTypes}
              onChange={handleVocalTypeChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const vocalType = vocalTypes.find(vt => vt.vocalTypeId === value || vt.id === value);
                    return (
                      <Chip 
                        key={value} 
                        label={vocalType?.vocalTypeName || vocalType?.name || value} 
                        size="small" 
                      />
                    );
                  })}
                </Box>
              )}
            >
              {vocalTypes.map((vt) => (
                <MenuItem key={vt.vocalTypeId || vt.id} value={vt.vocalTypeId || vt.id}>
                  {vt.vocalTypeName || vt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={3}>
          <FormControlLabel
            control={
              <Checkbox
                name="hasDriverLicencse"
                checked={formData.hasDriverLicencse || false}
                onChange={handleChange}
              />
            }
            label="Has Driver's License"
          />
        </Grid>

        <Grid item xs={3}>
          <FormControlLabel
            control={
              <Checkbox
                name="hasPassport"
                checked={formData.hasPassport || false}
                onChange={handleChange}
              />
            }
            label="Has Passport"
          />
        </Grid>

        {formData.sex === "Female" && (
          <>
            <Grid item xs={3}>
              <TextField
                label="Bust"
                name="bust"
                fullWidth
                value={formData.bust || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Waist"
                name="waist"
                fullWidth
                value={formData.waist || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Hips"
                name="hips"
                fullWidth
                value={formData.hips || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Dress Size"
                name="dress"
                fullWidth
                value={formData.dress || ""}
                onChange={handleChange}
              />
            </Grid>
          </>
        )}

        {formData.sex === "Male" && (
          <>
            <Grid item xs={3}>
              <TextField
                label="Suit Size"
                name="maleSuit"
                fullWidth
                value={formData.maleSuit || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Shirt Size"
                name="maleShirt"
                fullWidth
                value={formData.maleShirt || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Waist"
                name="waist"
                fullWidth
                value={formData.waist || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Inseam"
                name="maleInseam"
                fullWidth
                value={formData.maleInseam || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Shoe Size"
                name="maleShoe"
                fullWidth
                value={formData.maleShoe || ""}
                onChange={handleChange}
              />
            </Grid>
          </>
        )}

        <Grid container spacing={2} mt={2}>
          <Grid item>
            <Button onClick={onSkip}>Skip</Button>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={handleSubmit}>
              Next
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default BasicDetailsForm;